// [목적] folio-export 도메인 REST API 클라이언트 — POST/GET /folios/:id/export(/:exportId) 매핑 + PDF 다운로드
// [이유] FlowLine A2(49df7920): 저장된 폴리오 PDF 내보내기(요청/폴링/다운로드). BE 계약은
//        apps/api/src/modules/folio-export/dto/export-request.schema.ts — 이 스키마는 packages/types 로
//        이관되지 않아(BE 전용 zod), FE 는 동일 구조를 로컬 zod 로 재정의해 응답을 parse 한다
//        (folio-social.client.ts commentPageSchema 와 동일 관례 — BE 전용 응답 wrapper 로컬 재정의).
//        2026-09-18 보안 조치(security/2026-09-17_m5-m11-final.md O-4): 기존 <a href> 직접 navigate 방식은
//        same-origin 이어도 브라우저 네비게이션에 x-anon-session-id 헤더가 실리지 않아 익명 export 다운로드가
//        항상 404 였고, access token 만료 시 refresh 없이 401 로 실패했다 — downloadFolioExport() 를 fetch
//        기반으로 추가해 http.ts 의 isSameOrigin/refreshSessionOnce 를 user-export.client.ts 선례와 동일하게 재사용.
// [사이드 이팩트] downloadFolioExport() 는 same-origin 이면 x-anon-session-id 헤더를 첨부하고, 401 시
//                refreshSessionOnce() 로 1회 재시도한다. FolioExportButton 은 <a href> navigate 대신
//                이 함수가 반환한 Blob 을 URL.createObjectURL 로 감싸 다운로드하도록 변경됨.
// [수정 시 고려사항] export-request.schema.ts 가 packages/types 로 이관되면 로컬 zod 를 그것으로 교체할 것.
//                   format 은 BE 가 'pdf' 만 허용 — png 확장 시 이 파일의 리터럴도 함께 갱신 필요.
//                   409/410 은 ApiError(status) 로 throw — 410(만료)은 호출부가 "다시 내보내기" 안내로 분기할 것.

import { z } from 'zod';

import {
  ApiError,
  NetworkError,
  apiBase,
  fetchWithEnvelope,
  isSameOrigin,
  refreshSessionOnce,
} from './http.js';
import { ensureAnonSessionId } from './anonSession.js';

const exportCreateResponseSchema = z.object({
  exportId: z.string(),
  status: z.literal('queued'),
});
export type FolioExportCreateResponse = z.infer<typeof exportCreateResponseSchema>;

const exportStatusResponseSchema = z.object({
  status: z.enum(['queued', 'running', 'done', 'failed']),
  pdfUrl: z.string().nullable(),
  expiresAt: z.string().nullable(),
  errorCode: z.string().nullable(),
});
export type FolioExportStatusResponse = z.infer<typeof exportStatusResponseSchema>;

/**
 * [목적] POST /folios/:id/export {format:'pdf'} — 내보내기 요청(202 + {exportId, status})
 * [이유] A2: 클릭 즉시 큐 적재 — 렌더는 BE BullMQ 워커가 비동기 처리.
 * [사이드 이팩트] 없음(호출 1회당 새 exportId 발급).
 * [수정 시 고려사항] BE RateLimitGuard(anon 5/min, user 15/min) — 초과 시 ApiError(429 상당) throw.
 */
export async function requestFolioExport(folioId: string): Promise<FolioExportCreateResponse> {
  const data = await fetchWithEnvelope<unknown>(`/folios/${folioId}/export`, {
    method: 'POST',
    body: JSON.stringify({ format: 'pdf' }),
  });
  return exportCreateResponseSchema.parse(data);
}

/**
 * [목적] GET /folios/:id/export/:exportId — 내보내기 상태 폴링
 * [이유] A2: 2초 간격 폴링 — status가 done|failed 가 될 때까지 호출부(useFolioExport)가 반복 호출.
 * [사이드 이팩트] 없음(순수 조회).
 * [수정 시 고려사항] status='done' 일 때만 pdfUrl/expiresAt 채워짐(BE 계약).
 */
export async function getFolioExportStatus(
  folioId: string,
  exportId: string,
): Promise<FolioExportStatusResponse> {
  const data = await fetchWithEnvelope<unknown>(`/folios/${folioId}/export/${exportId}`);
  return exportStatusResponseSchema.parse(data);
}

/**
 * [목적] GET /folios/:id/export/:exportId/download 절대 URL 조립
 * [이유] A2: <a download> 로 직접 열어 브라우저가 스트리밍 다운로드를 처리하도록 함(blob 수동 처리 회피).
 * [사이드 이팩트] 없음(순수 함수)
 * [수정 시 고려사항] apiBase 가 빈 문자열이면 상대경로(same-origin) — fetchWithEnvelope 와 동일 규칙.
 */
export function buildFolioExportDownloadUrl(folioId: string, exportId: string): string {
  return `${apiBase}/folios/${folioId}/export/${exportId}/download`;
}

export interface FolioExportDownloadResult {
  blob: Blob;
  filename: string;
}

/**
 * [목적] Content-Disposition 헤더에서 filename 추출 — 없으면 folio-<id>.pdf 기본값
 * [이유] user-export.client.ts parseFilename 과 동일 관례 — BE 가 filename="..." 형태로 내려줌.
 * [사이드 이팩트] 없음(순수 함수).
 * [수정 시 고려사항] BE 가 RFC 5987 filename* 인코딩을 쓰게 되면 이 정규식으로 잡히지 않음(현재 ASCII 계약).
 */
function parseExportFilename(contentDisposition: string | null, folioId: string): string {
  const match = contentDisposition?.match(/filename="?([^";]+)"?/i);
  if (match?.[1]) return match[1];
  return `folio-${folioId}.pdf`;
}

/**
 * [목적] GET /folios/:id/export/:exportId/download — PDF 를 Blob 으로 fetch (401 refresh 재시도 포함)
 * [이유] O-4 조치: 기존 <a href> navigate 는 same-origin 익명 세션 헤더 미첨부·AT 만료 시 refresh 미동작
 *        문제가 있었다. fetchWithEnvelope 는 성공 응답이 StreamableFile(raw binary)이라 envelope
 *        {data,error} 파싱과 맞지 않으므로 user-export.client.ts exportMyData() 와 동일하게 raw fetch 사용.
 * [사이드 이팩트] same-origin 이면 x-anon-session-id 헤더 첨부 + credentials:'include'.
 *                401 이면 http.ts refreshSessionOnce() 로 1회만 재시도(무한 재귀 방지 _isRetry 플래그).
 *                409/410 등 비정상 응답은 ApiError(status,code,message) throw — 410 은 만료(재발급 필요).
 * [수정 시 고려사항] 호출부(FolioExportButton)가 Blob 을 URL.createObjectURL 로 감싼 뒤 반드시 revokeObjectURL 할 것.
 */
export async function downloadFolioExport(
  folioId: string,
  exportId: string,
  _isRetry = false,
): Promise<FolioExportDownloadResult> {
  const url = buildFolioExportDownloadUrl(folioId, exportId);
  const headers = new Headers();
  const sameOrigin = isSameOrigin(url);
  if (sameOrigin) {
    const sessionId = ensureAnonSessionId();
    if (sessionId) headers.set('x-anon-session-id', sessionId);
  }

  let res: Response;
  try {
    res = await fetch(url, {
      method: 'GET',
      headers,
      ...(sameOrigin ? { credentials: 'include' } : {}),
    });
  } catch (err) {
    throw new NetworkError(err);
  }

  if (res.status === 401 && !_isRetry) {
    await refreshSessionOnce();
    return downloadFolioExport(folioId, exportId, true);
  }

  if (!res.ok) {
    let code = `HTTP_${res.status}`;
    let message = `HTTP ${res.status}`;
    try {
      const body = (await res.json()) as { error?: { code?: string; message?: string } };
      code = body.error?.code ?? code;
      message = body.error?.message ?? message;
    } catch {
      // 비-JSON 에러 응답(410 등)은 기본 코드/메시지 유지
    }
    throw new ApiError(res.status, code, message);
  }

  const blob = await res.blob();
  const filename = parseExportFilename(res.headers.get('content-disposition'), folioId);
  return { blob, filename };
}
